import { FC, MouseEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import Upload from 'rc-upload';
import { RcFile } from 'rc-upload/lib/interface';
import { useWxWork } from '@cloud/wxwork-workbench';
import PageLayout from '@/layouts/page';
import Button from '@/components/Button';
import Tooltip from '@/components/Tooltip';
import Toast from '@/components/Toast';
import { ReactComponent as IPlus } from '@/assets/icons/plus.svg';
import { upload } from './service';
import styles from './index.less';


const MAX_SIZE = 1024 * 1024;

export interface ReportUnavailabilityProps {
  onBack?: () => void;
}

const tipText = '通过客户的手机号添加客户微信时，显示“该用户不存在”，需上传该截图，便于管理者知晓和客户标记，上传后此号码会自动移除';

const ReportUnavailabilityPage: FC<ReportUnavailabilityProps> = ({ onBack }) => {
  const signature = useWxWork();
  const [file, setFile] = useState<RcFile | void>();
  const [url, setUrl] = useState<string | void>();
  const [loading, setLoading] = useState<boolean>(false);
  let { phoneNumber } = useParams<{ phoneNumber?: string }>();

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setUrl(reader.result.toString());
      }
    } else {
      setUrl();
    }
  }, [file]);

  const beforeUpload = (f: RcFile) => {
    if (f.type !== 'image/jpeg' && f.type !== 'image/png') {
      Toast.error('只支持png、jpg格式');
    } else if (f.size > MAX_SIZE) {
      Toast.error('图片最大支持 1MB');
    } else {
      setFile(f);
    }
    return false;
  }

  const handleDeleteFile = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setFile();
  }

  const handleSubmit = async () => {
    if (!loading) {
      try {
        setLoading(true);
        await upload({ number: phoneNumber, file, auth: signature });
        onBack();
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <PageLayout onBack={onBack}>
      <div className={styles.main}>
        <div className={styles.tip}>
          <label className={styles.label}>说明</label>
          <span className={styles.text}>
            {tipText}
            <Tooltip
              title={<img width={248} height={440} src='https://brand-guide.shuyun.com/IAM/83a45c08722d.jpg' alt='' />}
              placement='bottom'
              trigger={['hover', 'click', 'focus']}
              type='white'
            >
              <span className={styles.exampleText}>示例图</span>
            </Tooltip>
          </span>
        </div>
        <div className={styles.uploadWrapper}>
          <Upload
            type='drag'
            accept='image/png, image/jpeg'
            beforeUpload={beforeUpload}
            className={classNames(styles.upload, { [styles.hasImg]: file })}
            style={{ backgroundImage: `url(${url})` }}
          >
            {file ? null : (
              <>
                <div className={styles.plus}><IPlus /></div>
                <div className={styles.text}>上传号码无效截图</div>
              </>
            )}
            <Button
              type='link'
              disabled={loading || !file}
              className={styles.deleteButton}
              onClick={handleDeleteFile}
            >删除</Button>
          </Upload>
          <div className={styles.imageTip}>图片格式：最大1MB，支持png、jpg格式</div>
        </div>
        <Button
          type='primary'
          size='large'
          disabled={loading || !file}
          onClick={handleSubmit}
          className={styles.submitButton}
        >{loading ? '上传中...' : '提交'}</Button>
      </div>
    </PageLayout>
  );
}

export default ReportUnavailabilityPage;